/**
 * Pro: the full desktop layout. Menu bar, toolbar with search, sidebar, the
 * open view, the details panel, the transport and the status bar.
 *
 * At narrower widths it gives way gracefully: the details panel floats over
 * the list instead of squeezing it, then the sidebar collapses to icons.
 */

import { useEffect, useState } from "react";
import { useApp } from "../core/app";
import { Icon } from "../shell/Icons";
import { MenuBar, WebBrand, WebTrailing, showMenuBar } from "../shell/MenuBar";
import { InboxButton } from "../shell/Inbox";
import { MOD } from "../shell/menus";
import { SearchField } from "../shell/SearchField";
import { Sidebar } from "../shell/Sidebar";
import { StatusBar } from "../shell/StatusBar";
import { Transport } from "../shell/Transport";
import { TrackPanel } from "../views/TrackPanel";
import { OWN_PANEL, ViewHost } from "./ViewHost";
import { useRegistry } from "../core/registry";

function useWidth() {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
}

export function Toolbar() {
  const app = useApp();
  return (
    <div className="nv-toolbar" role="toolbar" aria-label="Toolbar">
      <WebBrand />
      <button className={`nv-iconbtn ${app.sidebar ? "on" : ""}`} aria-pressed={app.sidebar} title={`Sidebar (${MOD}+B)`} onClick={() => app.setSidebar(!app.sidebar)}>
        <Icon name="sidebar" />
      </button>
      <button className="nv-iconbtn" title="Songs" onClick={() => app.navigate("songs")}>
        <Icon name="home" />
      </button>
      <SearchField />
      <InboxButton />
      <button className={`nv-iconbtn ${app.inspector ? "on" : ""}`} aria-pressed={app.inspector} title={`Details panel (${MOD}+J)`} onClick={() => app.setInspector(!app.inspector)}>
        <Icon name="panel" />
      </button>
      <WebTrailing />
    </div>
  );
}

export function ProLayout() {
  const app = useApp();
  const width = useWidth();
  const focused = app.library.tracks.find((t) => t.id === app.focused) ?? null;
  const { views } = useRegistry();
  const panel = app.inspector && !OWN_PANEL.has(app.nav.view) && !views.find((v) => v.id === app.nav.view)?.ownPanel;
  const floating = width < 1180;
  const narrowSide = width < 900;
  return (
    <div className={`nv-app nv-pro ${narrowSide ? "narrow" : ""}`}>
      {showMenuBar() && <MenuBar />}
      <Toolbar />
      <div className={`nv-main ${app.sidebar ? "with-side" : ""} ${panel && !floating ? "with-panel" : ""}`}>
        {app.sidebar && <Sidebar />}
        <div className="nv-viewport">
          <ViewHost />
        </div>
        {panel && (
          <div className={floating ? "nv-floating-panel" : "nv-panel-slot"}>
            <TrackPanel track={focused} onClose={() => app.setInspector(false)} />
          </div>
        )}
      </div>
      <Transport />
      <StatusBar />
    </div>
  );
}
